/**
 * Derive a safe, human-readable download filename for a timeline audio clip
 * from its `sourcePath`.
 *
 * `sourcePath` (see `TimelineAudioClip.sourcePath` in ./types) may be:
 *  - a project-relative asset path (e.g. `audio/extracted-ab12cd34.wav`), or
 *  - an absolute http(s) URL, possibly signed with a query string
 *    (GCS v4 / R2 / legacy Supabase) and/or carrying a `#` fragment.
 *
 * Order matters:
 *  1. Reduce to the path portion FIRST (drop query string + hash), then take
 *     the basename. Doing this before decoding avoids an encoded `?`/`#` inside
 *     the real filename being mistaken for a delimiter after decode and
 *     truncating the name.
 *  2. Safely `decodeURIComponent` the basename — falls back to the raw basename
 *     when the string contains a malformed percent-escape (which would throw).
 *  3. Sanitise surviving path separators, control characters, and
 *     filesystem-reserved characters (`<>:"/\|?*`) to `_`.
 *  4. Fall back to `'audio-clip'` when nothing usable remains.
 *
 * NOTE: we deliberately do NOT synthesise a file extension. `TimelineAudioClip`
 * carries no MIME type, so an extension cannot be reliably recovered when the
 * path has none — inventing one (`.mp3`/`.wav`) would be a lie about the bytes.
 */

// C0 controls (\x00-\x1f), DEL + C1 controls (\x7f-\x9f), filesystem-reserved
// chars, and path separators. Authored with \x hex escapes so the source stays
// ASCII-clean.
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_CHARS = /[\x00-\x1f\x7f-\x9f<>:"/\\|?*]/g

export function deriveAudioClipFilename(
  sourcePath: string | null | undefined,
): string {
  const FALLBACK = 'audio-clip'
  if (!sourcePath) return FALLBACK

  // 1. Reduce to the path portion (no query/hash).
  let pathPart = sourcePath
  if (/^https?:\/\//i.test(sourcePath)) {
    try {
      pathPart = new URL(sourcePath).pathname
    } catch {
      // Malformed absolute URL — fall through and strip query/hash manually.
      pathPart = sourcePath
    }
  }
  // For relative paths (and the malformed-absolute fallback), strip everything
  // from the first `?` or `#`. Absolute URLs already dropped these via
  // `.pathname`, so this is a harmless no-op for them.
  pathPart = pathPart.split(/[?#]/, 1)[0]

  const rawBasename = pathPart.split('/').pop() ?? ''

  // 2. Safe decode.
  let decoded: string
  try {
    decoded = decodeURIComponent(rawBasename)
  } catch {
    decoded = rawBasename
  }

  // 3. Sanitise.
  const sanitised = decoded.replace(UNSAFE_FILENAME_CHARS, '_').trim()

  // 4. Fallback.
  return sanitised.length > 0 ? sanitised : FALLBACK
}
